import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SignaturePad from './SignaturePad';
import EsignConsentGate from './EsignConsentGate';
import AccountConsentCheckbox from './AccountConsentCheckbox';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 THE VERSION OF EACH SCREEN'S WORDS, sent with every acceptance and recorded by the server
// (Jesse, 2026-10-15). Must equal backend/constants/offerReviewContent.js SCREENS[*].version —
// test/offerReviewContent.test.js fails when the words or the versions drift apart.
export const CONTENT_VERSIONS = Object.freeze({
  le_ack: '2026-10-15',
  rights: '2026-10-15.2',
  services: '2026-10-15',
  purchase_model: 'purchase-model-1',
  purchase_model_resale: '2026-10-15',   // a file that signed a resale adjustment under a retired option
  intent: '2026-10-15',
});

/**
 * OfferAcceptedReview — full-screen 6-screen overlay shown after the
 * listing agent accepts the offer (loan_stage = 'offer_accepted').
 *
 * Steps:
 *   1. Loan Estimate (DocMagic SSO iframe + highlight cards; polls
 *      le-status until ready, falls through after 30s)
 *   2. Borrower Rights & Resources (counseling + ECOA appraisal +
 *      GLBA privacy — no signature required)
 *   3. Transaction Services Disclosure (1 checkbox + IP capture)
 *   4. Doorly Transaction Disclosure (4 required checkboxes — model,
 *      resale adjustment, alternative financing, voluntary)
 *   5. Intent to Proceed (timestamps + IP capture)
 *   6. Confirmation (next-steps list + complete handler that fires
 *      B-C PSA auto-gen + admin alert for A-B PSA routing)
 *
 * Resume-from-step is driven by users.offer_review_current_step plus
 * a derivation in the backend GET endpoint that picks the first
 * incomplete milestone. Mounted via HeroOfferAccepted's "Review &
 * Continue" CTA on Dashboard.
 */
// Mobile breakpoint detector — matches Tailwind's `sm` (640px). Used
// throughout the overlay to swap padding, font sizes, and the LE
// iframe min-height to mobile-friendly values without rewriting every
// inline-style block into Tailwind classes.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

export default function OfferAcceptedReview({ token, onClose, onComplete, borrowerName }) {
  const [step, setStep]       = useState(1);
  const [hydrating, setHydrating] = useState(true);
  const [data, setData]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const isMobile = useIsMobile();
  // Screen 3 — single services disclosure checkbox
  const [cServices, setCServices] = useState(false);
  // Screen 4 — two combined required checkboxes.
  // c1 = model + resale adjustment; c2 = alternative financing + voluntary.
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  // 🔑 E-SIGN CONSENT BEFORE ANY SCREEN, and again on every accept (a 451 brings the gate back).
  const [needsConsent, setNeedsConsent] = useState(false);
  // 'consent_events' → the E-Sign consent screen; 'disclosure' → the account consent (Terms, Privacy
  // and E-Sign at the current version). Asking for the wrong one would loop.
  const [consentWhich, setConsentWhich] = useState(null);
  const openedRef = useRef(false);

  const headers = { Authorization: `Bearer ${token}` };
  // A 451 at accept: re-read the verdict so the right consent screen shows. (refresh is defined
  // below; it is only called from event handlers, after it exists.)
  const onRefused = (err) => {
    if (err?.response?.status === 451) { setNeedsConsent(true); refreshRef.current && refreshRef.current(); return true; }
    return false;
  };
  const refreshRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      // ?opened=1 once per mount: the server records that the review was opened (and by whom).
      const opened = openedRef.current ? '' : '?opened=1';
      openedRef.current = true;
      const r = await axios.get(`${API_URL}/api/borrower/offer-accepted-review${opened}`, { headers });
      setNeedsConsent(r.data?.esign ? r.data.esign.allowed === false : false);
      setConsentWhich(r.data?.esign?.which || null);
      setData(r.data || {});
      const dataStep = Number(r.data?.current_step) || 1;
      setStep(Math.max(1, Math.min(6, dataStep)));
      // Hydrate prior selections so back-nav doesn't reset state.
      if (r.data?.services_disclosure_accepted) setCServices(true);
      if (r.data?.disclosure_accepted) { setC1(true); setC2(true); }
    } catch (_) {
      // First-load failures fall through to step 1 with default state.
    } finally { setHydrating(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { refreshRef.current = refresh; refresh(); }, [refresh]);

  const advance = async (nextStep) => {
    setSubmitting(true); setError('');
    try {
      await axios.patch(`${API_URL}/api/borrower/offer-accepted-review`,
        { step: nextStep, contentVersions: CONTENT_VERSIONS }, { headers });
      setStep(nextStep);
    } catch (err) {
      if (onRefused(err)) return;
      setError(err.response?.data?.error || 'Could not save your progress. Please try again.');
    } finally { setSubmitting(false); }
  };

  const goBack = () => { if (step > 1) setStep(step - 1); };
  const coborrower = !!(data && data.role === 'coborrower');

  const submitServices = async () => {
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API_URL}/api/borrower/offer-accepted-review/services-disclosure-acceptance`,
        { accepted: true, contentVersion: CONTENT_VERSIONS.services }, { headers });
      await advance(4);
    } catch (err) {
      if (onRefused(err)) { setSubmitting(false); return; }
      setError(err.response?.data?.error || 'Could not save acknowledgment.');
      setSubmitting(false);
    }
  };

  const submitDisclosures = async () => {
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API_URL}/api/borrower/offer-accepted-review/disclosure-acceptance`, {
        checkbox_1: c1,  // model_understood + resale_adjustment combined
        checkbox_2: c2,  // alternative_financing + voluntary combined
        contentVersion: Number(data.resale_adjustment) > 0 ? CONTENT_VERSIONS.purchase_model_resale : CONTENT_VERSIONS.purchase_model,
      }, { headers });
      await advance(5);
    } catch (err) {
      if (onRefused(err)) { setSubmitting(false); return; }
      setError(err.response?.data?.error || 'Could not save acknowledgments.');
      setSubmitting(false);
    }
  };

  const submitIntent = async () => {
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API_URL}/api/borrower/offer-accepted-review/intent-to-proceed`,
        { contentVersion: CONTENT_VERSIONS.intent }, { headers });
      await advance(6);
    } catch (err) {
      if (onRefused(err)) { setSubmitting(false); return; }
      setError(err.response?.data?.error || 'Could not record intent to proceed.');
      setSubmitting(false);
    }
  };

  const submitComplete = async () => {
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API_URL}/api/borrower/offer-accepted-review/complete`, {}, { headers });
      onComplete && onComplete();
    } catch (err) {
      if (onRefused(err)) { setSubmitting(false); return; }
      setError(err.response?.data?.error || 'Could not complete the review. Please try again.');
      setSubmitting(false);
    }
  };

  // ── Layout primitives ────────────────────────────────────────────────────
  // Mobile (< 640px) gets tighter horizontal padding (16px vs 24px) so
  // narrow viewports don't lose content width to gutters. Vertical
  // padding tightens slightly on mobile too. Body keeps its maxWidth
  // 720px constraint for desktop centering.
  const overlay = {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: '#fff', display: 'flex', flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };
  const header = {
    flexShrink: 0,
    padding: isMobile ? '12px 16px' : '14px 24px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#fff',
  };
  const body = {
    flex: 1, overflowY: 'auto',
    padding: isMobile ? '20px 16px' : '32px 24px',
    maxWidth: 720, width: '100%', margin: '0 auto',
  };
  const footerActions = {
    flexShrink: 0,
    // the bottom inset is the overlay's (index.css .safe-modal-overlay), applied once; 0 is what env() gave at no inset
    padding: isMobile ? '12px 16px 0px' : '16px 24px',
    borderTop: '1px solid #f3f4f6',
    background: '#fff',
  };
  const primaryBtn = (disabled = false) => ({
    width: '100%', height: 52, padding: '0 24px',
    background: disabled ? '#93c5fd' : '#3a6ad9', color: '#fff',
    border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });
  const secondaryBtn = {
    width: '100%', height: 44, padding: '0 16px',
    background: 'transparent', color: '#6b7280',
    border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', marginTop: 10,
  };
  const dotRow = { display: 'flex', gap: 6, alignItems: 'center' };
  const dot = (active, complete) => ({
    width: 8, height: 8, borderRadius: '50%',
    background: complete ? '#3a6ad9' : active ? '#3a6ad9' : '#e5e7eb',
    opacity: complete ? 1 : active ? 1 : 0.5,
  });

  if (hydrating) {
    return (
      <div className="safe-modal-overlay" style={overlay}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
          Loading your review…
        </div>
      </div>
    );
  }

  return (
    <div className="safe-modal-overlay" style={overlay}>
      <div style={header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {step > 1 && (
            <button type="button" onClick={goBack} aria-label="Back"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 22, padding: 4, lineHeight: 1 }}>
              ←
            </button>
          )}
          {/* 🔑 A CO-BORROWER DOES SCREEN 1 ONLY (Part 1 item 7, Jesse 2026-10-25): the LE and his intent to proceed. */}
          <span data-testid="offer-review-step-label" style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
            {coborrower ? 'Your Loan Estimate' : `Step ${step} of 6`}
          </span>
        </div>
        {!coborrower && (
          <div style={dotRow}>
            {[1,2,3,4,5,6].map((n) => (
              <div key={n} style={dot(n === step, n < step)} />
            ))}
          </div>
        )}
      </div>

      <div style={body}>
        {error && (
          <div style={{ marginBottom: 18, padding: '12px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', color: '#9f1239', borderRadius: 10, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* 🚨 NO SCREEN WITHOUT E-SIGN CONSENT: the gate REPLACES the review until it is given. */}
        {needsConsent && consentWhich === 'disclosure' && (
          <AccountConsentRenewal token={token} userId={data.user_id} onConsented={() => { setNeedsConsent(false); refresh(); }} />
        )}
        {needsConsent && consentWhich !== 'disclosure' && (
          <EsignConsentGate
            token={token}
            documentLabel="your offer review and Loan Estimate"
            onConsented={() => { setNeedsConsent(false); refresh(); }}
          />
        )}
        {needsConsent && (
          <button type="button" onClick={onClose}
            style={{ width: '100%', height: 44, marginTop: 10, background: 'transparent', color: '#6b7280', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Save for later
          </button>
        )}
        {!needsConsent && step === 1 && <Screen1Le token={token} data={data} onRefresh={refresh} isMobile={isMobile} onConsentRequired={() => setNeedsConsent(true)} />}
        {!needsConsent && step === 2 && <Screen2BorrowerRights />}
        {!needsConsent && step === 3 && <Screen3Services accepted={cServices} setAccepted={setCServices} />}
        {!needsConsent && step === 4 && <Screen4Disclosure resaleAdjustment={data.resale_adjustment} c1={c1} c2={c2} setC1={setC1} setC2={setC2} />}
        {!needsConsent && step === 5 && <Screen5Intent />}
        {!needsConsent && step === 6 && <Screen6Confirmation borrowerName={borrowerName} />}
      </div>

      {!needsConsent && <div style={footerActions}>
        <div style={{ maxWidth: 720, margin: '0 auto', width: '100%' }}>
          {step === 1 && (
            <>
              {/* Step 1 Continue gates on le_intent_to_proceed_at — the
                  Step 1 LE-ITP signature timestamp specifically (TILA
                  acknowledgment). Step 5's purchase_intent_to_proceed_at
                  is a different consent and is no longer set by Step 1
                  signing the LE. data is refreshed by Screen1Le after a
                  successful sign so this flips automatically. Falls back
                  to the legacy intent_to_proceed_at for any rows where
                  the GET backfill hasn't migrated yet. */}
              {(() => {
                const leAck = data?.le_intent_to_proceed_at || data?.intent_to_proceed_at;
                return (
                  <>
                    <button type="button" data-testid="offer-review-step1-continue"
                      disabled={submitting || !leAck}
                      onClick={() => (coborrower ? (onComplete ? onComplete() : onClose()) : advance(2))}
                      style={primaryBtn(submitting || !leAck)}>
                      {submitting ? 'Saving…' : coborrower ? 'Done' : 'Continue'}
                    </button>
                    {coborrower && leAck && (
                      <p data-testid="offer-review-coborrower-done" style={{ margin: '8px 0 0', fontSize: 12.5, color: '#065f46', textAlign: 'center' }}>
                        Thank you. {data && data.primary_first_name ? data.primary_first_name : 'The primary borrower'} completes the rest of the review.
                      </p>
                    )}
                    {!leAck && (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                        Please review the Loan Estimate and sign your acknowledgment to continue.
                      </p>
                    )}
                  </>
                );
              })()}
              <button type="button" onClick={onClose} style={secondaryBtn}>Save for later</button>
            </>
          )}
          {step === 2 && (
            <>
              <button type="button" disabled={submitting} onClick={() => advance(3)} style={primaryBtn(submitting)}>
                {submitting ? 'Saving…' : 'Continue'}
              </button>
              <button type="button" onClick={onClose} style={secondaryBtn}>Save for later</button>
            </>
          )}
          {step === 3 && (
            <>
              <button type="button" disabled={submitting || !cServices} onClick={submitServices} style={primaryBtn(submitting || !cServices)}>
                {submitting ? 'Saving…' : 'Continue'}
              </button>
              <button type="button" onClick={onClose} style={secondaryBtn}>Save for later</button>
            </>
          )}
          {step === 4 && (
            <>
              <button type="button" disabled={submitting || !c1 || !c2} onClick={submitDisclosures} style={primaryBtn(submitting || !c1 || !c2)}>
                {submitting ? 'Saving…' : 'I Understand & Continue'}
              </button>
              <button type="button" onClick={onClose} style={secondaryBtn}>Save for later</button>
            </>
          )}
          {step === 5 && (
            <>
              <button type="button" disabled={submitting} onClick={submitIntent} style={primaryBtn(submitting)}>
                {submitting ? 'Saving…' : 'Yes, Proceed With My Home Purchase'}
              </button>
              <button type="button" onClick={onClose} style={secondaryBtn}>Save for later</button>
            </>
          )}
          {step === 6 && (
            <>
              <button type="button" disabled={submitting} onClick={submitComplete} style={primaryBtn(submitting)}>
                {submitting ? 'Finalizing…' : 'Go to Dashboard'}
              </button>
              <button type="button" onClick={onClose} style={secondaryBtn}>Save for later</button>
            </>
          )}
        </div>
      </div>}

      {/* ── Global footer (all screens) ──────────────────────────────────
          Persistent legal/help bar below the action buttons. Spec says
          background #f9fafb, border-top #e5e7eb, font 11/#9ca3af. */}
      <div style={{
        flexShrink: 0,
        background: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
        padding: '8px 16px',
        textAlign: 'center',
        fontSize: 11,
        color: '#9ca3af',
      }}>
        <a href="https://app.godoorly.com/privacy" target="_blank" rel="noopener noreferrer" style={legalLink}>Privacy Policy</a>
        <span style={{ margin: '0 6px' }}> · </span>
        <a href="https://www.godoorly.com/terms-of-service" target="_blank" rel="noopener noreferrer" style={legalLink}>Terms of Service</a>
        <span style={{ margin: '0 6px' }}> · </span>
        Questions? <a href="mailto:support@godoorly.com" style={legalLink}>support@godoorly.com</a>
      </div>
    </div>
  );
}

const legalLink = { color: 'inherit', textDecoration: 'none' };

// ── The account consent, renewed here when it is missing, stale or withdrawn ─────
// The same checkbox (and the same documents, at their current versions) as the application's
// certification step; recorded by the same route.
function AccountConsentRenewal({ token, userId, onConsented }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setSaving(true); setErr('');
    try {
      await axios.post(`${API_URL}/api/account-consent`, { agreed: true },
        { headers: { Authorization: `Bearer ${token}` } });
      onConsented && onConsented();
    } catch (e) {
      setErr(e.response?.data?.message || e.response?.data?.error || 'Could not record your consent. Please try again.');
      setSaving(false);
    }
  };
  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 8 }}>Before we continue</div>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
        To receive your Loan Estimate and the documents in this review electronically, please agree to our current
        Terms, Privacy Policy and E-Sign Consent.
      </p>
      <AccountConsentCheckbox userId={userId} token={token} checked={checked} onChange={(v) => { setChecked(v); setErr(''); }} error={err} />
      <button type="button" disabled={!checked || saving} onClick={save}
        style={{ marginTop: 12, width: '100%', height: 48, background: !checked || saving ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: !checked || saving ? 'not-allowed' : 'pointer' }}>
        {saving ? 'Saving…' : 'Agree and continue'}
      </button>
    </div>
  );
}

// ── Screen 1: Loan Estimate ──────────────────────────────────────────────────
function Screen1Le({ token, data, onRefresh, isMobile, onConsentRequired }) {
  const [leAvailable, setLeAvailable] = useState(!!data.le_available);
  const [pdfObjectUrl, setPdfObjectUrl] = useState('');
  const [needsEsignConsent, setNeedsEsignConsent] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const pollAttemptsRef = useRef(0);
  const objectUrlRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let cancelled = false;
    let interval = null;
    // Step 1 is view-only — fetch the LE as a plain PDF blob and load
    // it into the iframe via a same-origin object URL. This bypasses
    // DocMagic's SignDocs SSO flow entirely (which depends on the
    // PackageCreated webhook landing before a sign URL can be built);
    // pdf_base64 is populated synchronously by the DMD generation, so
    // this works the moment le-status reports available=true.
    const fetchPdf = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/borrower/offer-accepted-review/le-pdf`, {
          headers,
          responseType: 'blob',
        });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        objectUrlRef.current = url;
        setPdfObjectUrl(url);
      } catch (err) {
        // 🚨 451 IS E-SIGN CONSENT, NOT A MISSING DOCUMENT. Without this branch
        // the refusal falls into the 404 path below and the borrower watches
        // "Loading your Loan Estimate viewer…" forever, with the real reason
        // visible only in a server log.
        if (!cancelled && err?.response?.status === 451) {
          setNeedsEsignConsent(true);
          return;
        }
        // /le-pdf 404 means pdf_base64 isn't populated yet even though the
        // parent endpoint reported le_available=true (race between
        // package_status flipping non-pending and the base64 landing, or
        // a stale availability response). Flip back to polling so the
        // session auto-recovers when the PDF finally lands — without this
        // the iframe sits on "Loading your Loan Estimate viewer…" forever.
        if (!cancelled) setLeAvailable(false);
      }
    };
    const poll = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/borrower/offer-accepted-review/le-status`, { headers });
        if (!cancelled && r.data?.available) {
          setLeAvailable(true);
          fetchPdf();
        }
      } catch (_) {}
    };
    if (leAvailable) {
      fetchPdf();
    } else {
      interval = setInterval(() => {
        pollAttemptsRef.current += 1;
        if (pollAttemptsRef.current >= 6) setShowFallback(true);
        poll();
      }, 5000);
    }
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leAvailable]);

  const downloadLE = () => {
    if (!pdfObjectUrl) return;
    window.open(pdfObjectUrl, '_blank', 'noopener');
  };

  // Highlight values from the GET endpoint. fmt$ falls back to "—" when null.
  const fmt$ = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return '—';
    return '$' + Math.round(n).toLocaleString();
  };
  const fmtPct = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return '—';
    return n.toFixed(2) + '%';
  };
  const cardStyle = {
    flex: 1, minWidth: 0,
    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: '12px 14px',
  };

  return (
    <>
      <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 800, color: '#111827' }}>Review your loan terms</h1>
      <p style={{ margin: '0 0 16px', fontSize: 15, color: '#374151', lineHeight: 1.7 }}>
        This is your official Loan Estimate. It outlines your projected loan terms, monthly payment, and closing costs based on your accepted offer.
      </p>
      {!leAvailable && !showFallback && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 32, textAlign: 'center', marginBottom: 16 }}>
          <div style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid #e5e7eb', borderTopColor: '#3a6ad9', borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>Your Loan Estimate is being prepared…</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>This usually takes less than a minute.</p>
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      )}
      {!leAvailable && showFallback && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 18, marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 14, color: '#7a5200', lineHeight: 1.6 }}>
            Your Loan Estimate is still being prepared. You can continue and review it later in your Documents tab.
          </p>
        </div>
      )}
      {leAvailable && pdfObjectUrl && (
        <div style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#f9fafb' }}>
          {/* Native PDF viewer hash params — keep the toolbar (toolbar=1),
              collapse the left nav panel (navpanes=0), default zoom 75%
              so the 11-page LE fits comfortably in the 60vh iframe and
              the borrower doesn't have to zoom-out to see the page edge
              numbers. Hash is appended to the blob: URL; browsers that
              ignore PDF hash params just render at their default zoom. */}
          {/* iframe height — desktop default 60vh, minHeight 400. Mobile
              drops minHeight to 280 so a 568h phone (visible area ~440px
              after header + footer chrome) doesn't get the iframe
              eating the entire visible body. */}
          <iframe title="Loan Estimate"
            src={`${pdfObjectUrl}#toolbar=1&navpanes=0&zoom=${isMobile ? 50 : 75}`}
            style={{ width: '100%', height: '60vh', minHeight: isMobile ? 280 : 400, border: 'none' }} />
        </div>
      )}
      {/* 🚨 CONSENT BEFORE THE DOCUMENT, and it REPLACES the viewer rather than
          sitting beside it. The server already refused the bytes; rendering an
          empty viewer alongside would suggest the document is loading. */}
      {needsEsignConsent && (
        <EsignConsentGate
          token={token}
          documentLabel="your Loan Estimate"
          onConsented={() => { setNeedsEsignConsent(false); setLeAvailable(false); }}
        />
      )}
      {leAvailable && !pdfObjectUrl && !needsEsignConsent && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, marginBottom: 16, fontSize: 14, color: '#374151' }}>
          Loading your Loan Estimate viewer…
        </div>
      )}

      {/* Highlight cards — Monthly Payment / Cash to Close / Interest Rate */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Monthly Payment</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{fmt$(data.monthly_payment)}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cash to Close</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{fmt$(data.cash_to_close)}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Interest Rate</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{fmtPct(data.interest_rate)}</div>
        </div>
      </div>

      <p style={{ margin: '12px 0 0', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
        This is not your final loan. Final terms will be confirmed before closing.
      </p>
      {leAvailable && pdfObjectUrl && (
        <button type="button" onClick={downloadLE}
          style={{ marginTop: 14, padding: '10px 18px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Open / Download
        </button>
      )}

      {/* Intent to Proceed signature — gates Step 1 Continue. The
          existing Step 5 click-acknowledge is the broader home-purchase
          authorization; this Step 1 capture is specifically the LE
          acknowledgment + intent to proceed with the loan. Two distinct
          consents. Hidden once intent_to_proceed_at is set on the
          server (which Step 5 also satisfies). */}
      <ItpSignaturePanel token={token} data={data} onRefresh={onRefresh} isMobile={isMobile} onConsentRequired={onConsentRequired} />
    </>
  );
}

// ── ITP Signature panel (Step 1) ─────────────────────────────────────────────
// Reuses the shared SignaturePad component (same one PSASigningModal
// uses). Typed-name field must match the legal name on the LE; the
// server stamps the signature image + typed name + ISO timestamp + IP
// onto the last page of the LE PDF and persists the result as
// document_type='itp_signed_le'.
function ItpSignaturePanel({ token, data, onRefresh, isMobile, onConsentRequired }) {
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Step 1 LE-ITP signature timestamp specifically. Falls back to the
  // legacy intent_to_proceed_at for rows that haven't been backfilled
  // (will hit the upstream backfill on the next user-record touch).
  const itpAt = data?.le_intent_to_proceed_at || data?.intent_to_proceed_at || null;
  const itpName = data?.intent_to_proceed_typed_name || null;

  if (itpAt) {
    // Already signed Step 1. intent_to_proceed_typed_name is set by the
    // canvas-signature path; pre-backfill borrowers who completed via
    // legacy combined column may not have a typed name on record.
    const niceDate = (() => {
      try {
        return new Date(itpAt).toLocaleString('en-US', {
          month: 'long', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit',
        });
      } catch (_) { return itpAt; }
    })();
    return (
      <div style={{
        marginTop: 24, background: '#f0fdf4', border: '1px solid #86efac',
        borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 22 }}>✅</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#16a34a' }}>
            Intent to Proceed Acknowledged
          </div>
          <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>
            {itpName ? `Signed by ${itpName} on ${niceDate}` : `Signed ${niceDate}`}
            {' · Timestamped copy saved to your documents'}
          </div>
        </div>
      </div>
    );
  }

  const canSubmit = !!signatureDataUrl && !!typedName.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError('');
    try {
      await axios.post(
        `${API_URL}/api/borrower/offer-accepted-review/intent-to-proceed-signature`,
        { signatureDataUrl, typedName: typedName.trim(), contentVersion: CONTENT_VERSIONS.le_ack },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onRefresh) await onRefresh();
    } catch (err) {
      if (err?.response?.status === 451 && onConsentRequired) { setSubmitting(false); onConsentRequired(); return; }
      setError(err.response?.data?.error || 'Could not save your acknowledgment. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      marginTop: 24,
      background: '#f9fafb', border: '1px solid #e5e7eb',
      borderRadius: 10, padding: 20,
    }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 6 }}>
        Acknowledge &amp; Sign
      </div>
      <div style={{ fontSize: 13, color: '#374151', marginBottom: 14, lineHeight: 1.6 }}>
        By signing below, I confirm that I have received and reviewed this Loan Estimate
        and wish to proceed with this loan.
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
          Your full legal name
        </div>
        <input
          type="text"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder="As shown on the Loan Estimate"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            fontSize: 14,
            boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        {signatureDataUrl ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={signatureDataUrl} alt="Signature"
              style={{ height: 70, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 4 }} />
            <button type="button" onClick={() => setSignatureDataUrl(null)} disabled={submitting}
              style={{
                padding: '8px 14px', background: '#fff', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}>
              Redo
            </button>
          </div>
        ) : (
          // Signature canvas width — desktop 480px fits comfortably
          // inside the 720px-max body. Mobile shrinks to the smaller
          // of (viewport − 56px gutter, 360px) so the canvas never
          // overflows a 320px phone but stays roomy enough on a 414px
          // one. The 56px subtracts ~16px outer body padding + ~12px
          // inner panel padding on each side.
          <SignaturePad
            width={isMobile ? Math.min(360, (typeof window !== 'undefined' ? window.innerWidth : 360) - 56) : 480}
            height={120}
            confirmLabel="Save Signature"
            onSave={setSignatureDataUrl}
            disabled={submitting}
          />
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#991b1b' }}>
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        style={{
          width: '100%',
          padding: '11px 24px',
          background: canSubmit ? '#3a6ad9' : '#e5e7eb',
          color: canSubmit ? '#fff' : '#9ca3af',
          border: 'none', borderRadius: 8,
          fontSize: 14, fontWeight: 700,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
        }}>
        {submitting ? 'Saving…' : 'I Acknowledge & Wish to Proceed →'}
      </button>
    </div>
  );
}

// ── Screen 2: Borrower Rights & Resources ───────────────────────────────────
function Screen2BorrowerRights() {
  const sectionHeading = { margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: '#111827' };
  const sectionBody = { margin: '0 0 10px', fontSize: 14, color: '#374151', lineHeight: 1.7 };
  const sectionDivider = { borderTop: '1px solid #e5e7eb', margin: '20px 0' };
  return (
    <>
      <h1 style={{ margin: '0 0 18px', fontSize: 24, fontWeight: 800, color: '#111827' }}>Your rights and resources</h1>

      {/* Section 1 — Homeownership Counseling */}
      <h2 style={sectionHeading}>Homeownership Counseling</h2>
      <p style={sectionBody}>
        You have the option to work with a housing counselor to better understand the homebuying process and your financial responsibilities.
      </p>
      <p style={sectionBody}>This resource is optional and available at no cost.</p>
      <a href="https://www.hud.gov/i_want_to/talk_to_a_housing_counselor" target="_blank" rel="noopener noreferrer"
        style={{ display: 'inline-block', padding: '10px 14px', background: '#eff6ff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
        Find a HUD-approved housing counselor →
      </a>

      <div style={sectionDivider} />

      {/* Section 2 — ECOA Appraisal Disclosure */}
      <h2 style={sectionHeading}>Your Appraisal Rights</h2>
      <p style={sectionBody}>
        You have the right to receive a copy of any appraisal or written valuation used in connection with your loan.
      </p>
      <p style={sectionBody}>
        We will automatically provide you with a copy promptly after it is completed.
      </p>

      <div style={sectionDivider} />

      {/* Section 2b — Home Loan Toolkit: a REMINDER only. The delivery of record is the
          step-3 send (backend/utils/sendApplicationDisclosures.js); this links the same
          CFPB edition (backend/templates/notices/home-loan-toolkit.json source_url). */}
      <h2 style={sectionHeading}>Your Home Loan Toolkit</h2>
      <p style={sectionBody}>
        {"The Consumer Financial Protection Bureau's guide to buying a home, which we provided when you applied."}
      </p>
      <a href="https://files.consumerfinance.gov/f/documents/cfpb_your-home-loan-toolkit_print_2026-08.pdf" target="_blank" rel="noopener noreferrer"
        style={{ display: 'inline-block', padding: '10px 14px', background: '#eff6ff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
        View Your Home Loan Toolkit →
      </a>

      <div style={sectionDivider} />

      {/* Section 3 — GLBA Privacy */}
      <h2 style={sectionHeading}>Your Privacy</h2>
      <p style={sectionBody}>
        Your personal information is handled in accordance with our Privacy Policy.
      </p>
      <a href="https://app.godoorly.com/privacy" target="_blank" rel="noopener noreferrer"
        style={{ display: 'inline-block', padding: '10px 14px', background: '#eff6ff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
        View Privacy Policy →
      </a>

      <p style={{ margin: '24px 0 0', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
        Doorly does not require you to use counseling services to move forward.
      </p>
    </>
  );
}

// ── Screen 3: Transaction Services Disclosure ──────────────────────────────
function Screen3Services({ accepted, setAccepted }) {
  return (
    <>
      <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 800, color: '#111827' }}>How your transaction is completed</h1>
      <p style={{ margin: '0 0 12px', fontSize: 15, color: '#374151', lineHeight: 1.7 }}>
        To complete your home purchase, Doorly coordinates certain third-party services as part of the transaction, including:
      </p>
      <ul style={{ margin: '0 0 18px', paddingLeft: 22, fontSize: 14, color: '#374151', lineHeight: 1.9 }}>
        <li>Title services</li>
        <li>Appraisal package</li>
      </ul>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
        These services are facilitated through designated providers selected by Doorly due to their ability to support the structure, timing, and execution of the purchase and resale transaction.
      </p>
      <p style={{ margin: '0 0 18px', fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
        Because of the nature of this transaction, these services are coordinated through Doorly's designated providers to ensure proper execution and timely closing. These providers are used to facilitate the transaction and are not optional within the Doorly program. Doorly does not receive referral fees or compensation from these providers.
      </p>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: '#fff', border: `1px solid ${accepted ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
          style={{ marginTop: 2, accentColor: '#3a6ad9', flexShrink: 0 }} />
        <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
          I understand that certain third-party services are coordinated through Doorly's designated providers as part of this transaction
        </span>
      </label>
    </>
  );
}

// ── Screen 4: Doorly Transaction Disclosure (2 combined checkboxes) ────────
function Screen4Disclosure({ resaleAdjustment, c1, c2, setC1, setC2 }) {
  const adj = resaleAdjustment != null ? `${resaleAdjustment}%` : '—';
  const withResale = Number(resaleAdjustment) > 0;
  const cb = (checked, onChange, label) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: '#fff', border: `1px solid ${checked ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', marginBottom: 10 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, accentColor: '#3a6ad9', flexShrink: 0 }} />
      <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>{label}</span>
    </label>
  );
  return (
    <>
      <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 800, color: '#111827' }}>Understanding your Doorly purchase</h1>
      <p style={{ margin: '0 0 12px', fontSize: 15, color: '#374151', lineHeight: 1.7 }}>
        Doorly works differently than a traditional lender:
      </p>
      <ul style={{ margin: '0 0 18px', paddingLeft: 22, fontSize: 14, color: '#374151', lineHeight: 1.9 }}>
        <li>Doorly purchases the property directly</li>
        {/* 🔑 (2026-10-21) The no-markup screen under the current option; the prior screen for a file that signed a
            resale adjustment under a retired option — for that file it is the true description. */}
        {withResale ? (
          <>
            <li>Doorly resells the property to you</li>
            <li>You receive a first-lien mortgage as part of that transaction</li>
            <li>The final purchase price includes a resale adjustment of <strong>{adj}</strong></li>
          </>
        ) : (
          <>
            <li>Doorly sells the property to you</li>
            <li>You receive a first-lien mortgage as part of that transaction</li>
            <li>{"The price you pay Doorly is the same as the contract price in Doorly's purchase agreement with the seller. Doorly does not add a markup."}</li>
            <li>In that purchase, Doorly may negotiate and receive seller-paid concessions, and the real estate brokerage representing Doorly may receive compensation from the seller. Those amounts are not passed on to you, and they do not increase your price.</li>
          </>
        )}
      </ul>
      {withResale
        ? cb(c1, setC1, "I understand how Doorly's purchase and resale model works and that the final price includes a resale adjustment")
        : cb(c1, setC1, "I understand how Doorly's purchase model works and that the price I pay is Doorly's contract price with the seller")}
      {cb(c2, setC2, 'I understand I may pursue alternative financing and I am choosing to proceed voluntarily')}
    </>
  );
}

// ── Screen 5: Intent to Proceed ────────────────────────────────────────────
function Screen5Intent() {
  return (
    <>
      <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 800, color: '#111827' }}>Ready to move forward?</h1>
      <p style={{ margin: '0 0 16px', fontSize: 15, color: '#374151', lineHeight: 1.7 }}>
        You're ready to move forward with your selected home.
        By continuing, you authorize Doorly to proceed with your loan and transaction, including sending funds to secure the property.
      </p>
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 16px' }}>
        <p style={{ margin: 0, fontSize: 14, color: '#7a5200', lineHeight: 1.6, fontWeight: 600 }}>
          ⚠ This step allows Doorly to move forward and formally execute the contract to secure your home.
        </p>
      </div>
    </>
  );
}

// ── Screen 6: Confirmation ─────────────────────────────────────────────────
function Screen6Confirmation({ borrowerName }) {
  return (
    <>
      <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 800, color: '#111827' }}>You're all set 🎉</h1>
      <p style={{ margin: '0 0 16px', fontSize: 15, color: '#374151', lineHeight: 1.7 }}>
        We're moving forward with your loan and home purchase{borrowerName ? `, ${borrowerName.split(' ')[0]}` : ''}.
      </p>
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px' }}>
        <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.04 }}>Next steps</p>
        <ul style={{ margin: 0, paddingLeft: 22, fontSize: 14, color: '#374151', lineHeight: 1.9 }}>
          <li>Complete Purchase Contract</li>
          <li>Send Down Payment</li>
          <li>Order Appraisal Package</li>
        </ul>
      </div>
    </>
  );
}
